'use client';

import { FC, useState } from 'react';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/ui/form';
import { FormError } from '@/components/form';

import { CreateProductSchema } from '@/schemas';
import { useTranslations } from '@/hooks/useTranslations';
import { TProductData } from '@/types/product';
import { editProduct } from '@/actions';
import { useProductContext } from '@/contexts/product-context';
import { useSubmitStatus } from '@/hooks/useSubmitStatus';
import { LoadingButton } from '@/components/loading-button';
import { ProductFormFields } from '@/features/products/_shared/product-form-fields';

interface IEditProduct {
  product: TProductData;
}

export const EditProductForm: FC<IEditProduct> = ({ product }) => {
  const { dispatch } = useProductContext();
  const { status, wrap } = useSubmitStatus();
  const common = useTranslations('Common');

  const [error, setError] = useState<string | undefined>('');

  const form = useForm<z.infer<typeof CreateProductSchema>>({
    resolver: zodResolver(CreateProductSchema),
    defaultValues: {
      id: product.id,
      name: product.name,
      price: +product.price,
      sku: product.sku ?? '',
      description: product.description ?? '',
      unit: product.unit ?? '',
    },
  });

  const onSubmit = (values: z.infer<typeof CreateProductSchema>) => {
    setError('');

    wrap(
      () =>
        new Promise<void>((resolve, reject) => {
          editProduct({ id: product?.id, ...values })
            .then((data) => {
              if (data.success) {
                resolve();

                setTimeout(() => {
                  dispatch({ type: 'CLOSE_EDIT_PRODUCT_POPUP' });
                  form.reset();
                }, 1500);
              } else {
                setError(data.error);
                reject();
              }
            })
            .catch(() => {
              setError('Something went wrong!');
              reject();
            });
        })
    );
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <ProductFormFields
            control={form.control}
            disabled={status === 'loading'}
          />
        </div>

        <FormError message={error} />

        <LoadingButton
          isFull={true}
          type="submit"
          status={status}
          idleText={common('confirm')}
        />
      </form>
    </Form>
  );
};
